import { isTempClipId } from '@src/services/WriteQueueService';
import { LogManager } from '@src/utility/Logger';

import type { ClipFragment } from '@src/services/AIClipProducer';
import type {
  ClipMetadata,
  OtherVideo,
  ReelSectionOrigin,
} from '@src/types.ts/story';

// Turns clip cards the creator selected in Content Studio ▸ Video Clips into
// the ClipFragments the existing combine (saveProducedClips → saveAsReel)
// consumes (PLAT-11049, epic DE11). Each fragment is built from clip metadata
// alone — no editor load, no transcript fetch — because clipJson already holds
// the source range the clip was cut from.

const logger = LogManager.getLogger('content:clipFragmentsFromClips');

/** `ClipFragment.transcriptPosition` is required by the type but only read when
 *  `startTime` is missing, which an eligible clip never is (see isClipCombinable). */
const NO_TRANSCRIPT_POSITION = { startIndex: -1, endIndex: -1 };

const hasUsableRange = (meta: Pick<ClipMetadata, 'startTime' | 'duration'>) =>
  typeof meta.startTime === 'number' &&
  typeof meta.duration === 'number' &&
  meta.duration > 0;

/**
 * The source ranges a clip contributes to a reel: one for an ordinary clip, one
 * per `subClips` entry for a reel (which flattens into that many Sections, per
 * epic DE15). Empty when the clip carries nothing croppable.
 */
function clipRanges(clipJson: ClipMetadata) {
  if (clipJson.isReel && clipJson.subClips?.length) {
    return clipJson.subClips.filter(hasUsableRange);
  }
  return hasUsableRange(clipJson) ? [clipJson] : [];
}

/**
 * PLAT-11067 — the lineage a piece carries out of this combine: the clip it was
 * taken from, in front of whatever that clip had already recorded.
 *
 * Prepending rather than replacing is what keeps the history of a reel built
 * from reels: the newest container is first, the passage's original clip last.
 */
function originChain(
  clip: OtherVideo,
  inherited: ReelSectionOrigin[] | undefined,
  isReel: boolean,
): ReelSectionOrigin[] {
  const self: ReelSectionOrigin = {
    clipId: clip.id ?? '',
    title: clip.title ?? '',
    ...(isReel && { isReel: true }),
  };
  return [self, ...(inherited ?? [])];
}

/** Stored title on the range, else the innermost origin entry's title. */
function passageTitle(
  storedTitle: string | undefined,
  inherited: ReelSectionOrigin[] | undefined,
): string | undefined {
  const trimmed = storedTitle?.trim();
  if (trimmed) return trimmed;
  return inherited?.length
    ? inherited[inherited.length - 1].title.trim() || undefined
    : undefined;
}

/**
 * Whether a clip card can take part in a combine. Exported so the card's
 * checkbox and the fragment builder cannot disagree about what is selectable.
 *
 * Excluded: clips still being written (no persisted range to crop from), and
 * clips with no usable source range at all — an uploaded or imported video is
 * not a passage of the story.
 */
export function isClipCombinable(clip: OtherVideo): boolean {
  if (!clip.id || isTempClipId(clip.id)) return false;
  if (!clip.clipJson) return false;
  return clipRanges(clip.clipJson).length > 0;
}

/**
 * Build the fragments for a reel, preserving the order the clips are given in
 * — which is selection order, not grid or source order (DE11). Clips that are
 * not combinable are skipped with a warning rather than throwing; the caller
 * gates on `isClipCombinable` up front, so this is a backstop.
 */
export function clipFragmentsFromClips(clips: OtherVideo[]): ClipFragment[] {
  const fragments: ClipFragment[] = [];

  for (const clip of clips) {
    // The same predicate the checkbox uses, not a re-derivation of half of it:
    // a backstop that tests less than the gate lets through exactly the clips
    // the gate meant to stop (a clip still being written has a range but no
    // persisted id to record provenance against).
    if (!isClipCombinable(clip)) {
      logger.warn('clipFragmentsFromClips: skipped clip that cannot combine', {
        videoId: clip.id,
        title: clip.title,
      });
      continue;
    }

    const clipJson = clip.clipJson;
    const ranges = clipJson ? clipRanges(clipJson) : [];

    const isReel = !!clipJson?.isReel && !!clipJson.subClips?.length;
    // A reel carrying its own structure has already answered what each Section
    // is called: the stored title, or nothing where a creator cleared one. Its
    // lineage and its own title seed a reel that never had names — they do not
    // overrule a name that was deliberately dropped (PLAT-11397).
    const namesOwned = isReel && !!clipJson?.reel;

    for (const range of ranges) {
      const inherited = 'origin' in range ? range.origin : undefined;
      const storedTitle = 'title' in range ? range.title : undefined;

      fragments.push({
        origin: originChain(clip, inherited, isReel),
        // Seeds the Section name (DE8), which `buildReelMetadata` persists on
        // the sub-clip. For a reel being combined that is the passage's own
        // clip, not the reel around it — naming every Section after the
        // container would give them all the same name.
        title: namesOwned
          ? (storedTitle?.trim() ?? '')
          : passageTitle(storedTitle, inherited) || clip.title || '',
        text: clip.transcriptionText ?? '',
        startTime: range.startTime,
        duration: range.duration,
        // Reel metadata is sourced from the story's source reference
        // (buildReelMetadata), not from per-fragment src.
        src: '',
        // `buildReelMetadata` takes the reel's playback id from its first
        // fragment. Without this a combined reel saves with none and the
        // preview has to fall back to the story's original.
        muxPlaybackId: clipJson?.muxPlaybackId,
        theme: range.theme ?? clipJson?.theme ?? '',
        speakerName: range.speakerName,
        speakerLabel: range.speakerLabel,
        impactScore: range.impactScore,
        transcriptPosition:
          clipJson?.transcriptPosition ?? NO_TRANSCRIPT_POSITION,
      });
    }
  }

  return fragments;
}
